// src/pages/FindOncologistsCityPage.jsx
import React, { useState, useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { 
  ShieldCheck, 
  MapPin, 
  Phone, 
  Stethoscope, 
  ArrowRight, 
  CheckCircle2, 
  Building2, 
  ChevronRight,
  Filter,
  Search,
  Users
} from 'lucide-react';
import MetaTags from '@/components/SEO/MetaTags';
import { SITE_ORIGIN } from '@/config/site';
import { 
  QUALIFIED_CITIES, 
  ONCOLOGISTS_BY_CITY,
  ONCOLOGY_TAXONOMIES 
} from '@/data/oncologistsData';
import Footer from '@/components/Footer';

export default function FindOncologistsCityPage() {
  const { stateSlug, citySlug } = useParams();
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const cityKey = `${stateSlug}/${citySlug}`;
  const cityInfo = QUALIFIED_CITIES.find((c) => c.stateSlug === stateSlug && c.citySlug === citySlug);
  const providers = ONCOLOGISTS_BY_CITY[cityKey] || [];

  const filteredProviders = useMemo(() => {
    return providers.filter((p) => {
      if (selectedSpecialty !== 'all') {
        const specLower = p.specialty.toLowerCase();
        if (selectedSpecialty === 'medical' && !specLower.includes('medical')) return false;
        if (selectedSpecialty === 'radiation' && !specLower.includes('radiation')) return false;
        if (selectedSpecialty === 'surgical' && !specLower.includes('surgical')) return false;
        if (selectedSpecialty === 'hematology' && !specLower.includes('hematology')) return false;
      }
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return (
        p.name.toLowerCase().includes(q) ||
        p.specialty.toLowerCase().includes(q) ||
        p.affiliation.toLowerCase().includes(q) ||
        p.practiceAddress.toLowerCase().includes(q)
      );
    });
  }, [providers, selectedSpecialty, searchTerm]);

  // ONLY allow cities with 10+ providers per requirements
  if (!cityInfo || providers.length < 10) {
    return <Navigate to={`/find-oncologists/${stateSlug}`} replace />;
  }

  const pageUrl = `${SITE_ORIGIN}${cityInfo.path}`;
  const stateUrl = `${SITE_ORIGIN}/find-oncologists/${stateSlug}`;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      name: `Best Oncologists in ${cityInfo.cityName}, ${cityInfo.stateAbbr} | Verified Provider Directory`,
      description: cityInfo.description,
      url: pageUrl,
      about: {
        '@type': 'MedicalSpecialty',
        name: 'Oncology',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
        { '@type': 'ListItem', position: 2, name: 'Find Oncologists', item: `${SITE_ORIGIN}/find-oncologists` },
        { '@type': 'ListItem', position: 3, name: cityInfo.stateName, item: stateUrl },
        { '@type': 'ListItem', position: 4, name: cityInfo.cityName, item: pageUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: filteredProviders.slice(0, 15).map((p, idx) => ({
        '@type': 'Physician',
        position: idx + 1,
        name: p.name,
        medicalSpecialty: p.specialty,
        telephone: p.phone,
        address: {
          '@type': 'PostalAddress',
          streetAddress: p.practiceAddress,
          addressLocality: p.city,
          addressRegion: p.state,
          postalCode: p.postalCode,
          addressCountry: 'US',
        },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-cream-paper text-charcoal font-sans antialiased">
      <MetaTags
        title={`Best Oncologists in ${cityInfo.cityName}, ${cityInfo.stateAbbr} | Verified Directory | ByOnco`}
        description={cityInfo.description}
        path={cityInfo.path}
        jsonLd={jsonLd}
      />

      {/* Top Breadcrumb Navigation */}
      <nav aria-label="Breadcrumb" className="border-b border-border-mist bg-white/60 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between text-xs sm:text-sm">
          <ol className="flex items-center space-x-2">
            <li>
              <Link to="/" className="text-gray-500 hover:text-forest-ink transition">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/find-oncologists" className="text-gray-500 hover:text-forest-ink transition">
                Find Oncologists
              </Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to={`/find-oncologists/${stateSlug}`} className="text-gray-500 hover:text-forest-ink transition">
                {cityInfo.stateName}
              </Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-forest-ink font-semibold" aria-current="page">{cityInfo.cityName}</li>
          </ol>
          <div className="flex items-center gap-3">
            <Link 
              to="/cancer-second-opinion" 
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-forest-ink text-white text-xs font-medium hover:bg-forest-shadow transition"
            >
              Get Virtual Second Opinion <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Header */}
      <header className="relative pt-10 pb-12 bg-gradient-to-b from-emerald-50/70 via-cream-paper to-cream-paper border-b border-border-mist">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100 text-emerald-900 text-xs sm:text-sm font-medium mb-4">
            <ShieldCheck className="w-4 h-4 text-emerald-700" />
            NPPES Verified · {cityInfo.cityName}, {cityInfo.stateAbbr} Oncology Practice Directory
          </div>

          <h1 className="text-3xl sm:text-5xl font-bold font-serif text-forest-ink tracking-tight mb-3">
            Best Oncologists in {cityInfo.cityName}, {cityInfo.stateAbbr}
          </h1>
          <p className="max-w-3xl mx-auto text-base sm:text-lg text-charcoal/85 leading-relaxed mb-6">
            Explore {providers.length}+ verified medical, surgical, and radiation oncologists practicing in {cityInfo.cityName}. Practice addresses, telephone numbers, and NPI identifiers.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 text-xs sm:text-sm text-charcoal/80">
            <span className="inline-flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-border-mist shadow-xs">
              <Users className="w-4 h-4 text-emerald-700" /> {providers.length}+ Verified Physicians
            </span>
            <span className="inline-flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-border-mist shadow-xs">
              <Building2 className="w-4 h-4 text-emerald-700" /> Major Academic Affiliations
            </span>
            <span className="inline-flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-border-mist shadow-xs">
              <Stethoscope className="w-4 h-4 text-emerald-700" /> Subspecialty Practice Locations
            </span>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-12">

        {/* Search & Filter */}
        <section className="bg-white p-5 rounded-2xl border border-border-mist shadow-xs space-y-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder={`Search ${cityInfo.cityName} oncologists by name, hospital, or street...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-forest-ink text-sm text-charcoal"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedSpecialty('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                  selectedSpecialty === 'all'
                    ? 'bg-forest-ink text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All ({providers.length})
              </button>
              <button
                onClick={() => setSelectedSpecialty('medical')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                  selectedSpecialty === 'medical'
                    ? 'bg-forest-ink text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Medical Onc
              </button>
              <button
                onClick={() => setSelectedSpecialty('radiation')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                  selectedSpecialty === 'radiation'
                    ? 'bg-forest-ink text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Radiation Onc
              </button>
            </div>
          </div>
        </section>

        {/* Provider Cards */}
        <section className="space-y-4">
          <div className="flex items-center justify-between text-xs sm:text-sm text-charcoal/80 mb-2">
            <span>Showing <strong>{filteredProviders.length}</strong> verified oncologists in {cityInfo.cityName}</span>
            <span className="text-gray-500">Source: CMS NPPES NPI Registry</span>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {filteredProviders.map((provider) => (
              <div
                key={provider.npi}
                className="p-5 rounded-2xl bg-white border border-border-mist hover:border-emerald-300 transition shadow-xs flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <h3 className="font-bold text-forest-ink text-base">
                        {provider.name}
                      </h3>
                      <p className="text-xs text-emerald-800 font-semibold flex items-center gap-1.5 mt-0.5">
                        <Stethoscope className="w-3.5 h-3.5" /> {provider.specialty}
                      </p>
                    </div>
                    <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-gray-100 text-gray-600 flex-shrink-0">
                      NPI: {provider.npi}
                    </span>
                  </div>

                  <p className="text-xs text-charcoal/70 mb-3 font-medium flex items-center gap-1">
                    <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{provider.affiliation}</span>
                  </p>

                  <div className="space-y-1 text-xs text-charcoal/80 bg-cream-paper/50 p-3 rounded-lg border border-border-mist">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <span>{provider.practiceAddress}, {provider.city}, {provider.state} {provider.postalCode}</span>
                    </div>
                    <div className="flex items-center gap-2 pt-1 border-t border-border-mist/60">
                      <Phone className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" />
                      <a href={`tel:${provider.phone}`} className="font-semibold text-emerald-800 hover:underline">
                        {provider.phone}
                      </a>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-border-mist flex items-center justify-between text-xs">
                  <span className="text-gray-500 font-mono text-[11px]">
                    Taxonomy: {provider.taxonomyCode}
                  </span>
                  <Link
                    to="/cancer-second-opinion"
                    className="inline-flex items-center gap-1 text-emerald-800 hover:text-forest-ink font-semibold"
                  >
                    Online Second Opinion <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Back Link to State Directory */}
        <div className="text-center pt-6">
          <Link
            to={`/find-oncologists/${stateSlug}`}
            className="inline-flex items-center gap-2 text-sm font-semibold text-forest-ink hover:text-emerald-800 transition"
          >
            ← View All {cityInfo.stateName} Oncologists
          </Link>
        </div>

      </main>

      <Footer />
    </div>
  );
}
